import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Play, FastForward, Users, Settings,
  Trophy, Zap, Award, Swords, Clock, Beaker
} from "lucide-react";
import { StatusBadge } from "./GameBadges";
import { TeamsManagement } from "./TeamsManagement";
import { DecisionZeroPanel } from "./DecisionZeroPanel";
import { StudentsManagement } from "./StudentsManagement";
import { LeaderboardView } from "./LeaderboardView";
import { EventsManagement } from "./EventsManagement";
import { ScenarioBuilder } from "./ScenarioBuilder";
import { GameSettings } from "./GameSettings";
import { CompetitionManager } from "./CompetitionManager";
import { TournamentManager } from "./TournamentManager";

export function GameManagement({ game, teams, leaderboard, competitions, onRefresh, onCreateTeamOpen, games, userRole }) {
  const { t } = useTranslation();
  const [advancing, setAdvancing] = useState(false);
  const [starting, setStarting] = useState(false);
  const isSuperadmin = userRole === "superadmin";

  const handleStartGame = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API}/games/${game.id}/start`);
      const assigned = res.data?.auto_assigned_hubs || [];
      if (assigned.length > 0) {
        toast.success(t("instructor.gameStartedWithHubs", {
          list: assigned.map(a => `${a.team_name} → ${a.hub_id}`).join(", "),
        }), { duration: 10000 });
      } else {
        toast.success(t("instructor.gameStarted"));
      }
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start game");
    } finally {
      setStarting(false);
    }
  };

  const handleAdvanceQuarter = async () => {
    setAdvancing(true);
    try {
      const response = await axios.post(`${API}/games/${game.id}/advance`);
      toast.success(response.data.message);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance quarter");
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Game Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">{game.name}</h2>
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={game.status} />
            <span className="text-slate-500">
              {t("history.quarter")} {game.current_quarter} {t("instructor.of")} {game.quarters_count}
            </span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-500">{teams.length} teams</span>
            {game.auto_advance && game.quarter_schedule && (() => {
              const next = game.quarter_schedule.find(s => s.quarter === game.current_quarter && !s.advanced);
              if (next?.scheduled_at) {
                return (
                  <>
                    <span className="text-slate-400">|</span>
                    <span className="text-amber-600 flex items-center gap-1 text-sm" data-testid="next-advance-time">
                      <Clock className="w-3.5 h-3.5" />
                      Next: {new Date(next.scheduled_at).toLocaleString()}
                      {game.schedule_timezone && <span className="text-slate-400 text-xs">({game.schedule_timezone})</span>}
                    </span>
                  </>
                );
              }
              return null;
            })()}
          </div>
        </div>

        <div className="flex gap-3">
          {game.status === "setup" && (
            <div className="flex flex-col items-end gap-1">
              <Button
                onClick={handleStartGame}
                disabled={starting || teams.length < 2}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="start-game-btn"
              >
                <Play className="w-4 h-4" />
                {starting ? t("instructor.starting") : t("instructor.startGame")}
              </Button>
              {teams.some(tm => (tm.members?.length || 0) > 0 && !tm.hub_id) && (
                <span className="text-xs text-amber-600 max-w-[260px] text-right" data-testid="pending-hubs-warning">
                  {t("instructor.pendingHubsWarning")}
                </span>
              )}
            </div>
          )}
          
          {game.status === "active" && (
            <Button
              onClick={handleAdvanceQuarter}
              disabled={advancing}
              className="gap-2"
              data-testid="advance-quarter-btn"
            >
              <FastForward className="w-4 h-4" />
              {advancing ? t("instructor.processing") : t("instructor.advanceQuarter")}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="teams" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 flex-wrap">
          <TabsTrigger value="teams" className="gap-2" data-testid="tab-teams">
            <Users className="w-4 h-4" />
            {t("nav.teams")}
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2" data-testid="tab-leaderboard">
            <Trophy className="w-4 h-4" />
            {t("nav.leaderboard")}
          </TabsTrigger>
          <TabsTrigger value="events" className="gap-2" data-testid="tab-events">
            <Zap className="w-4 h-4" />
            {t("nav.events")}
          </TabsTrigger>
          <TabsTrigger value="scenarios" className="gap-2" data-testid="tab-scenarios">
            <Beaker className="w-4 h-4" />
            {t("scenarioBuilder.title")}
          </TabsTrigger>
          {isSuperadmin && (
            <TabsTrigger value="competitions" className="gap-2" data-testid="tab-competitions">
              <Award className="w-4 h-4" />
              {t("nav.competitions")}
            </TabsTrigger>
          )}
          {isSuperadmin && (
            <TabsTrigger value="tournaments" className="gap-2" data-testid="tab-tournaments">
              <Swords className="w-4 h-4" />
              {t("nav.tournaments")}
            </TabsTrigger>
          )}
          <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
            <Settings className="w-4 h-4" />
            {t("nav.settings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="tab-content">
          <div className="space-y-6">
            {game.status === "setup" && (
              <DecisionZeroPanel gameId={game.id} teams={teams} onRefresh={onRefresh} />
            )}
            <TeamsManagement 
              teams={teams} 
              gameId={game.id}
              gameStatus={game.status}
              onCreateTeam={onCreateTeamOpen}
              onRefresh={onRefresh}
            />
            <StudentsManagement gameId={game.id} refreshKey={teams?.length} teams={teams} onRefresh={onRefresh} />
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="tab-content">
          <LeaderboardView leaderboard={leaderboard} />
        </TabsContent>

        <TabsContent value="events" className="tab-content">
          <EventsManagement game={game} onRefresh={onRefresh} />
        </TabsContent>

        <TabsContent value="scenarios" className="tab-content">
          <ScenarioBuilder games={games} />
        </TabsContent>

        {isSuperadmin && (
          <TabsContent value="competitions" className="tab-content">
            <CompetitionManager games={games} onRefresh={onRefresh} />
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="tournaments" className="tab-content">
            <TournamentManager competitions={competitions || []} onRefresh={onRefresh} />
          </TabsContent>
        )}

        <TabsContent value="settings" className="tab-content">
          <GameSettings game={game} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
